import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { Card, Spinner, EmptyList } from '../../components'
import { NotAvailableIcon } from 'assets'
import { useBindDispatch } from '../../hooks'
import { useTranslation } from 'react-i18next'
import { constants, getConnectUser, getStorage } from '../../helpers/'
import { findPermission } from '../../helpers/findPermission'
import { groupBy } from 'lodash'
import { ClientOrder } from './ClientOrder'
import { colors } from 'styles/colors'
import {
    NO_DATE,
    getSectionDateKey,
    processVillageClientGoalItems
} from './helpers/villageClientPacketHelpers'

export const VillageClientPacket = ({
    goalItems,
    modifyHandler,
    allowModification
}) => {
    const { t } = useTranslation()
    const { productListHandler } = useBindDispatch()
    const [localGoalItems, setLocalGoalItems] = useState([])
    const { productListData, loading, preFetchLoading } = useSelector(
        (store) => store.enrollment
    )

    const { zoneSFID } = getConnectUser()

    const userPermissions = getStorage(constants.USER_PERMISSIONS)
    const { UPDATE_ORDER } = constants

    const canUpdatePackage =
        findPermission(userPermissions, UPDATE_ORDER) && allowModification

    useEffect(() => {
        if (productListData?.length > 0) {
            setLocalGoalItems(
                processVillageClientGoalItems(
                    productListData,
                    zoneSFID,
                    goalItems
                )
            )
        }
    }, [goalItems, productListData])

    useEffect(() => {
        if (productListData?.length === 0 && !preFetchLoading) {
            productListHandler({ zoneSFID })
        }
    }, [])

    const sections = useMemo(() => {
        const goalItemsByStatus = groupBy(localGoalItems, (goalItem) => {
            if (goalItem.delivered === true) return 'delivered'
            if (goalItem.finished === true) return 'finished'
            return 'unfinished'
        })

        const buildGroup = (status) => {
            const statusItems = goalItemsByStatus[status] || []
            if (statusItems.length === 0) return []

            const byDate = groupBy(statusItems, getSectionDateKey)

            const datedKeys = Object.keys(byDate)
                .filter((dateKey) => dateKey !== NO_DATE)
                .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())

            const orderedKeys = byDate[NO_DATE]
                ? [...datedKeys, NO_DATE]
                : datedKeys

            return orderedKeys.map((dateKey) => ({
                status,
                goalItems: byDate[dateKey],
                dateKey,
                displayDate: dateKey === NO_DATE ? null : dateKey
            }))
        }

        return [
            ...buildGroup('unfinished'),
            ...buildGroup('finished'),
            ...buildGroup('delivered')
        ]
    }, [localGoalItems])

    if (loading) {
        return (
            <Spinner
                aria-label="loading-indicator"
                size="90"
                pageSpinner={true}
                fullscreen={true}
            />
        )
    }

    const displayFilteredGoalItems = (goalItemSections) => (
        <>
            {goalItemSections.map(
                ({
                    status,
                    goalItems: sectionGoalItems,
                    dateKey,
                    displayDate
                }) => (
                    <div
                        key={`${dateKey}-${status}`}
                        className="village-client-packet-section"
                    >
                        <ClientOrder
                            goalItems={sectionGoalItems}
                            status={status}
                            displayDate={displayDate}
                            t={t}
                            canUpdatePackage={canUpdatePackage}
                            modifyHandler={modifyHandler}
                        />
                    </div>
                )
            )}
        </>
    )

    return (
        <div className="village-client-packet-parent">
            <Card shadow className="village-client-packet-card no-lateral-padding">
                <div className="village-client-details-padding">
                    <div>
                        <p className="village-client-packet-header">
                            {t('clientDetails.packetsTitle')}
                        </p>
                    </div>
                    {localGoalItems.length > 0 ? (
                        displayFilteredGoalItems(sections)
                    ) : (
                        <EmptyList
                            className="empty-list--packets"
                            message={t('clientDetails.noPackets')}
                            icon={
                                <NotAvailableIcon
                                    width="84"
                                    height="84"
                                    color={colors.lightGrey}
                                />
                            }
                        />
                    )}
                </div>
            </Card>
        </div>
    )
}

VillageClientPacket.propTypes = {
    goalItems: PropTypes.array,
    modifyHandler: PropTypes.func,
    allowModification: PropTypes.bool
}
